import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeroCanvas } from "@/components/site/HeroCanvas";
import { PrimaryButton, Arrow, OutlineButton } from "@/components/site/Buttons";
import { LineReveal, Reveal, ClipImage } from "@/components/site/motion";
import { IMAGES, scrollToId, openInquiry, EASE } from "@/lib/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  return (
    <section ref={ref} id="home" data-testid="hero-section" className="px-3 sm:px-5 lg:px-8 pt-4 sm:pt-6">
      <div className="relative overflow-hidden rounded-[1.75rem] sm:rounded-[2.75rem] min-h-[86vh] flex items-center bg-gradient-to-b from-[#F5F2EA] via-[#F1EDE2] to-[#EAE5D6]">
        <div className="absolute inset-0 noise-overlay" />
        <HeroCanvas />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#EFEADF]/75 via-[#EFEADF]/10 to-transparent pointer-events-none" style={{ top: "74%" }} />
        <motion.div style={{ y: contentY }} className="relative z-10 px-6 sm:px-10 lg:px-20 pt-16 pb-48 sm:py-24 lg:py-32 w-full max-w-6xl">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="eyebrow mb-6 sm:mb-8"
          >
            LANDSCAPE DESIGN · INSTALLATION · OUTDOOR LIVING
          </motion.p>
          <LineReveal
            as="h1"
            delay={0.15}
            className="font-display font-light tracking-[-0.03em] leading-[1.04] text-[2.5rem] sm:text-7xl lg:text-[5.4rem] text-charcoal"
            lines={[
              <>Make <span className="font-semibold">your yard</span></>,
              <>the place <span className="font-serif italic font-normal">you want</span></>,
              <>to <span className="font-serif italic font-normal">spend time.</span></>,
            ]}
          />
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.6, ease: EASE }}>
            <p data-testid="hero-support" className="mt-7 text-base sm:text-xl text-charcoal/90 leading-relaxed max-w-2xl font-medium">
              A patio for dinner outside. A garden you enjoy coming home to. Phil Gray Landscaping brings your ideas together with custom landscape design and installation in Dixon, Davis, Vacaville, and nearby communities.
            </p>
            <p data-testid="hero-capabilities-line" className="mt-4 font-mono text-[11px] sm:text-xs tracking-[0.16em] text-charcoal/75 leading-relaxed max-w-xl">
              CUSTOM DESIGN · PATIOS & PATHS · PLANTING · IRRIGATION
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <PrimaryButton testid="hero-send-inquiry-button" onClick={() => openInquiry()}>DISCUSS MY PROJECT <Arrow /></PrimaryButton>
              <OutlineButton testid="hero-explore-solutions-button" onClick={() => scrollToId("solutions")} className="font-body font-semibold text-sm tracking-wide bg-cream/60 backdrop-blur-sm">
                EXPLORE OUR SERVICES
              </OutlineButton>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Para = ({ children, delay }) => (
  <Reveal delay={delay}>
    <p className="text-sm sm:text-base text-charcoal/70 leading-relaxed">{children}</p>
  </Reveal>
);

export const BrandStatement = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const y2 = useTransform(scrollYProgress, [0, 1], [110, -70]);
  const y3 = useTransform(scrollYProgress, [0, 1], [30, -30]);
  return (
    <section ref={ref} data-testid="brand-statement" className="px-6 sm:px-10 lg:px-16 pt-10 sm:pt-16 pb-16 sm:pb-36 max-w-[1700px] mx-auto">
      <div className="flex items-baseline justify-between gap-6 mb-10 sm:mb-14">
        <p className="eyebrow">DESIGNED AROUND THE WAY YOU LIVE</p>
        <p className="hidden sm:block font-mono text-[11px] tracking-[0.22em] text-charcoal/60">PHIL GRAY LANDSCAPING</p>
      </div>
      <LineReveal
        as="h2"
        className="font-display font-light tracking-[-0.03em] leading-[1.04] text-4xl sm:text-6xl lg:text-[5.2rem] text-charcoal max-w-6xl"
        lines={[
          <><span className="font-semibold">Your yard</span> has room</>,
          <>for <span className="font-serif italic font-normal">something better.</span></>,
        ]}
      />
      <div className="mt-16 lg:mt-32 grid lg:grid-cols-12 gap-10 lg:gap-10 items-start">
        <motion.div style={{ y: y1 }} className="lg:col-span-5">
          <ClipImage image={IMAGES.brandTall} className="aspect-[3/4] w-full" parallax={7} sizes="(max-width: 1024px) 100vw, 40vw" />
          <p className="mt-4 font-mono text-[11px] tracking-[0.22em] text-charcoal/60">OUTDOOR SPACES TO ENJOY</p>
        </motion.div>
        <motion.div style={{ y: y3 }} className="lg:col-span-4 lg:pt-40 space-y-7">
          <Reveal>
            <p className="text-lg sm:text-2xl font-medium text-charcoal leading-snug tracking-[-0.01em]">
              Maybe you want a place to gather with friends, a more welcoming entrance, or a garden that feels easier to care for. Start with what would make the biggest difference to you.
            </p>
          </Reveal>
          <Para delay={0.1}>The right plan connects the details: where you sit, how you move through the space, what you plant, and how you water it.</Para>
          <Para delay={0.18}>Phil Gray Landscaping is a family-owned business offering landscape design and installation, with services that bring planting and outdoor features together.</Para>
          <Para delay={0.22}>Bring your ideas, a few photos, or a list of what you want to change. You do not need a finished design to begin the conversation.</Para>
          <Reveal delay={0.26}>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <PrimaryButton testid="brand-tell-us-button" onClick={() => openInquiry()}>TELL US ABOUT YOUR YARD <Arrow /></PrimaryButton>
              <OutlineButton testid="brand-explore-capabilities-button" onClick={() => scrollToId("capabilities")}>EXPLORE THE POSSIBILITIES</OutlineButton>
            </div>
          </Reveal>
        </motion.div>
        <motion.div style={{ y: y2 }} className="lg:col-span-3 lg:pt-64">
          <ClipImage image={IMAGES.brandSmall} className="aspect-[4/5] w-full max-w-md" parallax={9} rounded="rounded-[2rem]" sizes="(max-width: 1024px) 100vw, 25vw" />
        </motion.div>
      </div>
    </section>
  );
};
